// src/components/job-engine/useRecurringSchedule.ts
import { useCallback, useEffect, useRef, useState } from "react";
import { isAxiosError } from "axios";
import { getRecurringSchedule } from "@/api/jobEngine";
import type { RecurringJob } from "@/api/jobEngine";

export interface UseRecurringScheduleResult {
  data: RecurringJob | null;
  isLoading: boolean;
  error: string | null;
  refetch: () => void;
}

export function useRecurringSchedule(id: string | undefined): UseRecurringScheduleResult {
  const [data, setData] = useState<RecurringJob | null>(null);
  const [isLoading, setIsLoading] = useState(Boolean(id));
  const [error, setError] = useState<string | null>(null);
  const [tick, setTick] = useState(0);
  const abortRef = useRef<AbortController | null>(null);
  const refetch = useCallback(() => setTick((t) => t + 1), []);

  useEffect(() => {
    if (!id) { setIsLoading(false); return; }
    abortRef.current?.abort();
    const ctrl = new AbortController();
    abortRef.current = ctrl;
    setIsLoading(true);
    setError(null);
    getRecurringSchedule(id, ctrl.signal)
      .then((r) => { if (!ctrl.signal.aborted) setData(r); })
      .catch((err: unknown) => {
        if (ctrl.signal.aborted) return;
        if (isAxiosError(err)) setError(err.response?.data?.title ?? "Failed to load schedule");
        else setError("An unexpected error occurred");
      })
      .finally(() => { if (!ctrl.signal.aborted) setIsLoading(false); });
    return () => ctrl.abort();
  }, [id, tick]);

  return { data, isLoading, error, refetch };
}
